import type { ColorPalette } from '@/features/colors/color.types';
import {
  NEUTRAL_COOL,
  NEUTRAL_GRAY,
  NEUTRAL_WARM,
} from '@/features/colors/neutrals';

// Professional predates design's palette sheets and has none of its own, so its
// shaded-cell (50) steps are derived rather than transcribed: each is its own 100
// wash mixed 18% toward its own 500 accent.
//
// Deepened rather than lightened, which inverts what the sheets do on their
// chromatic slots. Those lighten 50 past 100, but that only works while the wash
// has headroom above it, and Professional's washes are already paler than other
// palettes' shaded cells — lightening them further put three slots above Summer's
// weekend tint, so a blocked-out day would have read *lighter* than an ordinary
// weekend. Going darker is the same move design makes on every sheet's achromatic
// slot, for the same reason: where a pale tint cannot read as shading, the step
// goes down instead of up. The 18% lands each slot 13-22 luma under its wash,
// against the 22-40 the gray slots use — the gentler end, to suit "muted & calm".
//
// The 300 steps stay #FFFFFF. Nothing reads the muted step since the shaded-cell
// split removed getAccentMutedShade, so filling them would be inventing values no
// surface consumes.
export const professionalPalette: ColorPalette = {
  id: 'professional',
  name: 'Professional',
  description: 'Muted & calm',
  requiresPaidPlan: true,
  colors: {
    cool: { ...NEUTRAL_COOL },
    gray: { ...NEUTRAL_GRAY },
    warm: { ...NEUTRAL_WARM },
    color0: {
      neutral: { ...NEUTRAL_WARM },
      shade: {
        '50': '#E6D2DB',
        '100': '#F4ECF0',
        '300': '#FFFFFF',
        '500': '#A65E7E',
      },
    },
    color1: {
      neutral: { ...NEUTRAL_WARM },
      shade: {
        '50': '#E9D1D1',
        '100': '#F6EBEB',
        '300': '#FFFFFF',
        '500': '#B05A5A',
      },
    },
    color2: {
      neutral: { ...NEUTRAL_WARM },
      shade: {
        '50': '#EDDACE',
        '100': '#F7EFEA',
        '300': '#FFFFFF',
        '500': '#C07A4E',
      },
    },
    color3: {
      neutral: { ...NEUTRAL_WARM },
      shade: {
        '50': '#F0E8D5',
        '100': '#F9F5ED',
        '300': '#FFFFFF',
        '500': '#C9AE6A',
      },
    },
    color4: {
      neutral: { ...NEUTRAL_WARM },
      shade: {
        '50': '#E7E1D8',
        '100': '#F5F2EE',
        '300': '#FFFFFF',
        '500': '#A89274',
      },
    },
    color5: {
      neutral: { ...NEUTRAL_WARM },
      shade: {
        '50': '#D2DCD8',
        '100': '#ECF0EE',
        '300': '#FFFFFF',
        '500': '#5E8275',
      },
    },
    color6: {
      neutral: { ...NEUTRAL_COOL },
      shade: {
        '50': '#D2DDE6',
        '100': '#ECF0F4',
        '300': '#FFFFFF',
        '500': '#5E84A6',
      },
    },
    // The purple that replaced this palette's second, darker blue, which read as a
    // shade of color6's #5E84A6 rather than a colour of its own. Otherwise taken
    // whole from the retired Dusk palette — hence the only real 300 here, neither
    // derived nor stripped since nothing reads that step.
    //
    // Its 50 is the exception: Dusk's #F4F2F8 sat *above* the 100, so a shaded day
    // read lighter than the page background it borders. It now matches the wash,
    // which is where that background comes from.
    color7: {
      neutral: { ...NEUTRAL_COOL },
      shade: {
        '50': '#E9E5EE',
        '100': '#E9E5EE',
        '300': '#B7B0C5',
        '500': '#7C6F95',
      },
    },
  },
};
